import { useContext, useMemo } from 'react';
import { IntlShape } from 'react-intl';

import { permissions } from '@/app/contexts/permissions';
import { IntlContext } from '@/i18n';
import { IDriverBrief } from '@/modules/driver/model';
import { eventTracking } from '@/services/event-tracking';
import { CountryCode } from '@/shared/models/country-code';
import { useBookingActions } from '@/shared/utils/bookingLinksUtils';

import { ICard } from '../types';

import { TEST_NAME_ACADEMY } from './const';

const useAcademyCard = (
  { myDriver }: { myDriver: IDriverBrief | undefined },
  intl: IntlShape,
): ICard[] => {
  const { region } = useContext(IntlContext);

  const { openModalAction, goToExternalLinkAction } = useBookingActions({
    region,
    driver: myDriver ?? { id: '', firstName: '', lastName: '', email: '', phone: '' },
  });
  const { can } = useContext(permissions);

  return useMemo(() => {
    if (!myDriver) {
      return [];
    }

    return [
      {
        id: TEST_NAME_ACADEMY,
        cardStatus: myDriver.hasPassedAccreditation ? 'done' : 'todo',
        title: (() => {
          switch (region) {
            case CountryCode.France:
              return intl.formatMessage({
                defaultMessage: 'Sign up for a Pre Assessment Session',
                id: 'todo_academy_card_pre_assessment_title',
                description: 'Todo list academy card title',
              });
            default:
              return intl.formatMessage({
                defaultMessage: 'Enrol at the Wheely academy',
                id: 'g4cGRL',
                description: 'todo list card text',
              });
          }
        })(),
        desc: (() => {
          switch (region) {
            case CountryCode.ArabEmirates:
              return intl.formatMessage({
                defaultMessage:
                  'All chauffeurs must complete a 2-day training course at the Wheely Academy.',
                id: '2FoIj9',
                description: 'AE todo list card text',
              });
            case CountryCode.France:
              return intl.formatMessage({
                defaultMessage:
                  'You must complete a pre-assessment of your knowledge and motivation in a short interview at the Wheely Academy.',
                id: 'todo_academy_card_pre_assessment_description',
                description: 'Todo list academy card description',
              });
            case CountryCode.Russia:
              return intl.formatMessage({
                defaultMessage:
                  'All chauffeurs must complete a 2-day training course at the Wheely Academy and provide original documents.',
                id: '0inrt1',
                description: 'RU todo list card text',
              });
            case CountryCode.UnitedStates:
              return intl.formatMessage({
                defaultMessage:
                  'All chauffeurs are required to attend a 2-day in-person Wheely Academy course.',
                id: 'onboarding_academy_us_main_copy',
                description: 'US todo list card text',
              });
            default:
              return intl.formatMessage({
                defaultMessage:
                  'All chauffeurs must complete City Knowledge Test at the Wheely Academy followed by 2 days training course in-person.',
                id: 'onboarding_intro_city_test',
                description: 'GB todo list card text',
              });
          }
        })(),
        actionTitle: (() => {
          switch (region) {
            case CountryCode.GreatBritain:
              return intl.formatMessage({
                defaultMessage: 'Book City Knowledge Test',
                id: 'onboarding_intro_city_test_book',
                description: 'todo list card text',
              });
            case CountryCode.France:
              return intl.formatMessage({
                defaultMessage: 'Book Your Spot',
                id: 'todo_academy_card_pre_assessment_cta',
                description: 'Todo list academy card CTA button',
              });
            default:
              return intl.formatMessage({
                defaultMessage: 'CHOOSE TIME',
                id: '5f+rcd',
                description: 'todo list card text',
              });
          }
        })(),
        actionTracker: () => {
          eventTracking.trackSnowplow({
            event: 'Onboarding - Academy - Open',
          });
        },
        action: openModalAction,
        actionExternalLink: goToExternalLinkAction,
        isHiddenIfPartnerIsActive: true,
        isBlocked: !can.bookAnAccreditation,
      },
    ];
  }, [myDriver, intl, region, openModalAction, goToExternalLinkAction, can.bookAnAccreditation]);
};

export { useAcademyCard };
